/**
 * Builds the `multipart/form-data` body for the two §4.7 routes that take a file:
 * verify-facial-biometrics (`photoImg`) and declaration (`signatureImg`).
 *
 * Deliberately NOT a general object flattener. Both routes send at most one non-file
 * field, and the guide specifies that the one structured field there is
 * (`declarationItems`) arrives JSON-encoded as text — so serialising structure is the
 * call site's job, and anything nested reaching here is a mistake rather than a shape
 * to guess at. See the throw below.
 */

/** One binary part. `filename` is what makes the part bind as a file server-side. */
export interface VfsClientFilePart {
  name: string
  blob: Blob
  filename: string
}

/**
 * `fields` has already been through `deepUppercase` by the time it arrives — the
 * transform runs on the structured object, before anything is serialised, so multipart
 * changes how a body is packaged and nothing about DHA's capitals rule.
 */
export function toFormData(fields: unknown, files: readonly VfsClientFilePart[]): FormData {
  const form = new FormData()

  if (fields !== undefined && fields !== null) {
    if (typeof fields !== 'object' || Array.isArray(fields)) {
      throw new TypeError('A multipart body must be an object of scalar fields.')
    }

    for (const [key, value] of Object.entries(fields as Record<string, unknown>)) {
      // Dropped rather than sent as the literal "undefined", matching how
      // `buildVfsClientUrl` treats an absent query value.
      if (value === undefined || value === null) continue

      if (typeof value === 'string') {
        form.append(key, value)
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        form.append(key, String(value))
      } else {
        /**
         * The guard that makes the Blob-in-body defect unreachable.
         *
         * `deepUppercase` walks a Blob as a plain object and `Object.entries(blob)` is
         * empty — every property is on the prototype — so a Blob passed in `fields`
         * would already have been flattened to `{}` upstream, and this would append
         * "[object Object]" over it. Binary belongs in `files`; structure belongs in a
         * JSON string the caller builds.
         */
        throw new TypeError(
          `Cannot send '${key}' as a form field: only strings, numbers and booleans are ` +
            'supported. JSON-encode structured data at the call site, and pass binary as a file part.'
        )
      }
    }
  }

  // Files last, mirroring the order a DOM form would submit. The spec makes part order
  // insignificant, so this is for legibility on the wire rather than correctness.
  for (const file of files) {
    form.append(file.name, file.blob, file.filename)
  }

  return form
}
